import React from 'react'
import { AuthenticatorRouteComponentKey } from '@aws-amplify/ui-react-core'
import { MOCKED_USER, MOCKED_USER_EMAIL, MOCKED_USER_PHONE } from './aws-amplify-auth.tsx'

// Simple pass-through UI component mocks
export const Text = ({ children, ...props }: any) => <span data-testid={props['data-testid']} {...props}>{children}</span>
export const View = ({ children, ...props }: any) => <div data-testid={props['data-testid']} {...props}>{children}</div>
export const Flex = ({ children, ...props }: any) => <div data-testid={props['data-testid']} {...props}>{children}</div>
export const Button = ({ children, onClick, ...props }: any) => <button onClick={onClick} data-testid={props['data-testid']} {...props}>{children}</button>
export const Heading = ({ children, ...props }: any) => <h1 {...props}>{children}</h1>
export const Input = ({ ...props }: any) => <input {...props} />
export const Loader = (props: any) => <div>Loading...</div>
export const Table = ({ children, ...props }: any) => <table {...props}>{children}</table>
export const TableHead = ({ children, ...props }: any) => <thead {...props}>{children}</thead>
export const TableBody = ({ children, ...props }: any) => <tbody {...props}>{children}</tbody>
export const TableRow = ({ children, ...props }: any) => <tr {...props}>{children}</tr>
export const TableCell = ({ children, ...props }: any) => <td {...props}>{children}</td>
export const Pagination = (props: any) => <div>Pagination</div>
export const Divider = (props: any) => <hr />
export const Card = ({ children, ...props }: any) => <div {...props}>{children}</div>
export const Grid = ({ children, ...props }: any) => <div {...props}>{children}</div>
export const SliderField = ({ label, onChange, defaultValue, formatValue, ...props }: any) => (
  <div>
    <label>{label}</label>
    <input type="range" defaultValue={defaultValue} onChange={(e) => onChange?.(Number(e.target.value))} {...props} />
  </div>
)
export const TextField = ({ label, errorMessage, hasError, ...props }: any) => (
  <div>
    <label>{label}</label>
    <input type="text" aria-invalid={hasError} {...props} />
    {hasError && errorMessage && <span>{errorMessage}</span>}
  </div>
)
export const Link = ({ children, ...props }: any) => <a {...props}>{children}</a>
export const Alert = ({ children, ...props }: any) => <div role="alert" {...props}>{children}</div>
export const SelectField = ({ children, label, labelHidden, ...props }: any) => (
  <label>
    {label}
    <select aria-label={typeof label === 'string' ? label : undefined} {...props}>{children}</select>
  </label>
)
export const ToggleButtonGroup = ({ children, value, onChange, ...props }: any) => (
  <div role="group" data-value={value} {...props}>{children}</div>
)
export const ToggleButton = ({ children, value, ...props }: any) => (
  <button type="button" data-value={value} {...props}>{children}</button>
)
export const Badge = ({ children, ...props }: any) => <span {...props}>{children}</span>
export const Placeholder = ({ ...props }: any) => <div data-testid="placeholder" {...props} />
export const SwitchField = ({ label, checked, onChange, isDisabled, ...props }: any) => (
  <label {...props}>
    <input type="checkbox" checked={checked} onChange={onChange} disabled={isDisabled} />
    {label}
  </label>
)
const AccordionBase = ({ children, ...props }: any) => <div {...props}>{children}</div>
AccordionBase.Container = ({ children, ...props }: any) => <div {...props}>{children}</div>
AccordionBase.Item = ({ children, ...props }: any) => <div {...props}>{children}</div>
AccordionBase.Trigger = ({ children, ...props }: any) => <button {...props}>{children}</button>
AccordionBase.Content = ({ children, ...props }: any) => <div {...props}>{children}</div>
AccordionBase.Icon = () => <span>▼</span>
export const Accordion = AccordionBase
export const Image = (props: any) => <img alt="" {...props} />
export const useTheme = () => ({ tokens: { space: { xs: '4px' }, colors: { background: { tertiary: '#f0f0f0' } } } })

// Mock context that matches Amplify's types
const mockAuthContext = {
  user: MOCKED_USER,
  route: 'authenticated' as AuthenticatorRouteComponentKey,
  authStatus: 'authenticated',
  isPending: false,
  signOut: async () => { },
  error: '',
  hasValidationErrors: false,
  socialProviders: [],
  totpSecretCode: null,
  unverifiedContactMethods: {
    email: MOCKED_USER_EMAIL,
    phone_number: MOCKED_USER_PHONE,
  },
  validationErrors: {
    password: 'XXXXXXXX is required'
  }
}

export function useAuthenticator(selector?: any): any {
  const context = React.useContext(AuthenticatorContext)
  if (!context) {
    throw new Error('`useAuthenticator` must be used inside an `Authenticator.Provider`.')
  }
  const value = selector ? selector(context) : context
  const result = {
    ...context,
    ...value,
    authStatus: 'authenticated',
    route: 'authenticated' as AuthenticatorRouteComponentKey,
    user: MOCKED_USER,
    signOut: async () => { },
    totpSecretCode: null,
    unverifiedContactMethods: {
      email: MOCKED_USER_EMAIL,
      phone_number: MOCKED_USER_PHONE,
    },
    validationErrors: {
      password: 'XXXXXXXX is required'
    },
    QRFields: {
      totpIssuer: 'mockIssuer',
      totpUsername: 'XXXXXXXXXXXX'
    },
    fields: [],
  }
  return result
}

// Create a React context for the mock authenticator
const AuthenticatorContext = React.createContext(mockAuthContext)

// Mock Authenticator component with Provider
export const Authenticator: React.FC<{ children: React.ReactNode }> & {
  Provider: React.FC<{ children: React.ReactNode }>
} = ({ children }) => {
  return (
    <AuthenticatorContext.Provider value={mockAuthContext}>
      {children}
    </AuthenticatorContext.Provider>
  )
}

Authenticator.Provider = ({ children }) => {
  return (
    <AuthenticatorContext.Provider value={mockAuthContext}>
      {children}
    </AuthenticatorContext.Provider>
  )
}


